import {
  cacheKeys,
  FIND_PRODUCT_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal } from '../../shared/components/assignment/assignmentModal';
import { ProductAddonsAssignList } from './productAddonsAssignList';
import { ProductFilesAssignList } from './productFilesAssignList';
import { ProductRoutinesAssignList } from './productRoutinesAssignList';

const ASSIGN_TO_PRODUCT = `
  mutation AssignToProduct($input: AssignToProductInput!) {
    product { assignToProduct(input: $input) { success message } }
  }
`;

export enum ProductAssignmentType {
  Routines = 'routines',
  Files = 'files',
  Addons = 'addons',
}

interface ProductAssignmentFormProps {
  productId: string;
  title: string;
  assignmentType: ProductAssignmentType;
  showTabs?: boolean;
  onComplete: () => void;
}

export const ProductAssignmentForm = ({
  productId,
  title,
  assignmentType,
  showTabs = true,
  onComplete,
}: ProductAssignmentFormProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();

  const { data, isPending } = useCustomQuery({
    query: FIND_PRODUCT_ASSIGNMENTS,
    cacheKey: ['product', 'assignments', productId],
    variables: { productId },
  });
  const found = data?.product?.findProductAssignments;

  const { mutateAsync: assignToProduct } = useCustomMutation({
    query: ASSIGN_TO_PRODUCT,
  });

  const save = (field: 'routineIds' | 'fileIds' | 'addonIds') =>
    async ({ selected }: { selected: string[] }) => {
      await assignToProduct(
        { input: { productId, workspaceId: workspace!.id, [field]: selected } },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: cacheKeys('product').all,
              exact: false,
            });
          },
        }
      );
    };

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !workspace?.id}
      initialTabKey={assignmentType}
      showTabs={showTabs}
      onComplete={onComplete}
      tabs={[
        {
          key: ProductAssignmentType.Routines,
          label: t('product.assignments.routines'),
          searchPlaceholder: t('product.assignments.routineSearchPlaceholder'),
          assignments: found?.routines,
          onSave: save('routineIds'),
          renderList: (p) => (
            <ProductRoutinesAssignList
              productId={productId}
              workspaceId={workspace!.id}
              keyword={p.keyword}
              maxHeight={p.maxHeight}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: ProductAssignmentType.Files,
          label: t('product.assignments.files'),
          searchPlaceholder: t('product.assignments.fileSearchPlaceholder'),
          assignments: found?.files,
          onSave: save('fileIds'),
          renderList: (p) => (
            <ProductFilesAssignList
              productId={productId}
              workspaceId={workspace!.id}
              keyword={p.keyword}
              maxHeight={p.maxHeight}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: ProductAssignmentType.Addons,
          label: t('product.assignments.addons'),
          searchPlaceholder: t('product.assignments.addonSearchPlaceholder'),
          assignments: found?.addons,
          onSave: save('addonIds'),
          renderList: (p) => (
            <ProductAddonsAssignList
              productId={productId}
              workspaceId={workspace!.id}
              keyword={p.keyword}
              maxHeight={p.maxHeight}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};
